import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  ACTIVE_INDICATOR_LIST,
  PropertyData,
  PropertyInformation,
  SidePanelTab,
  SidePanelView
} from '@store/search-board/search-board.model';
import { ImageLoadService } from '@shared/services/image-load.service';
import { FormatterService } from '@shared/services/formatter.service';
import { LabelValue, LayerInfo } from '@root/search/models/map.models';
import { MixpanelService } from '@shared/services/mixpanel.service';
import { select, Store } from '@ngrx/store';
import { selectIsPremiumViewAllowed, selectPropertyInformation, selectSidePanelState } from '@store/search-board/search-board.selector';
import { AppState } from '@root/app-state.module';
import { selectPreferenceValue, selectSelectedGeos } from '@store/user/user.selector';
import { ReportsMixPanelUtil } from '@root/reports/reports-mixpanel-util.module';
import { SetCurrentPageName } from '@store/search-board/search-board.actions';
import { selectedMapLayer } from '@store/map-layers.selector';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { tap, withLatestFrom } from 'rxjs/operators';
import { FieldCode } from '@root/search/models/display-fields.constant';
import { MapUtils } from '@shared/utils/map-utils';
import { MAX_NUMBER_OF_SAVED_PROPERTIES, PreferenceCode } from '@store/user/user.model';
import { CurrencyPipe } from '@angular/common';
import { PropertyUtil } from '@shared/utils/property-util';
import { UpdateSavedProperties } from '@store/saved-properties/saved-properties.actions';
import { showGlobalAlert } from '@shared/utils/global-alerts';

const YEAR_DURATION = 365;
const MAX_MORT_YEARS = 20;
const MAX_OWNER_YEARS = 20;

@UntilDestroy()
@Component({
  selector: 'rlst-info-window',
  templateUrl: './info-window.component.html',
  styleUrls: ['./info-window.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CurrencyPipe],
})
export class InfoWindowComponent implements OnInit {
  @Input()
  set propertyData(data: PropertyData) {
    this._propertyData = data;
    this.hasPhotos = this.formatterService.formatYes(data.MLS_PHOTO_IND) !== '';
    this.photoHidden = false;
    this.propertySummary = this.getPropertySummary(data);
  }
  get propertyData() {
    return this._propertyData;
  }
  @Input() parcelId: string;
  @Input() fipsCode: string;
  @Input() savedPropertyIdList: string[];
  @Input() set propertyItem(value: PropertyInformation) {
    this._propertyItem = value;
    this.isFavorite = this.isItemSaved(value);
  };
  @Input() savedProperties: PropertyInformation[];


  @Input() isInstanetLinkAvailable: boolean = false;
  @Input() isReverseLinkAvailable: boolean = false;

  @Output() closeWindow: EventEmitter<void> = new EventEmitter<void>();
  @Output() hideWindow: EventEmitter<void> = new EventEmitter();
  @Output() zoomIntoProperty: EventEmitter<void> = new EventEmitter<void>();
  @Output() getLotDimensions: EventEmitter<void> = new EventEmitter<void>();
  @Output() viewPhotos: EventEmitter<void> = new EventEmitter<void>();
  @Output() reverseLink: EventEmitter<void> = new EventEmitter<void>();
  @Output() instanetLink: EventEmitter<void> = new EventEmitter<void>();
  @Output() openReport: EventEmitter<void> = new EventEmitter<void>();
  @Output() loadParcelDisclaimer: EventEmitter<void> = new EventEmitter<void>();

  public propertySummary: Array<LabelValue> = [];
  public hasPhotos: boolean;
  public photoHidden: boolean = false;
  public selectedLayer: LayerInfo;
  public selectedProperty: PropertyData | any;
  public isPremiumSearchAvailable: boolean;
  public isFavorite: boolean = false;
  private _propertyData: PropertyData;
  private sidePanelTabOpened: SidePanelTab;
  private countyList: Array<string>;
  public _propertyItem: PropertyInformation;

  constructor(
    public readonly imageLoadService: ImageLoadService,
    public readonly formatterService: FormatterService,
    private readonly mixpanelService: MixpanelService,
    private readonly cd: ChangeDetectorRef,
    private readonly currencyPipe: CurrencyPipe,
    private readonly store$: Store<AppState>) { }

  public ngOnInit(): void {
    this.store$.pipe(
      select(selectPropertyInformation),
      withLatestFrom(this.store$.pipe(select(selectedMapLayer))),
      untilDestroyed(this),
    ).subscribe(([property, layer]: any) => {
      this.selectedLayer = layer[0];
      this.selectedProperty = property;
      this.cd.markForCheck();
    });

    this.store$.pipe(
      select(selectIsPremiumViewAllowed),
      untilDestroyed(this),
      withLatestFrom(
        this.store$.pipe(select(selectPreferenceValue(PreferenceCode.PG_RL_PREMIUM_SEARCH, PreferenceCode.PREF_RL_PREMIUM_SEARCH)))
      ),
      tap(([isPremiumViewAllowed, premiumLayerWorks]) => {
        this.isPremiumSearchAvailable = isPremiumViewAllowed && premiumLayerWorks === 'Y';
      })
    ).subscribe();
  }

  public close(): void {
    this.closeWindow.emit();
  }

  public zoomInto(): void {
    this.mixPanelServiceTracking('Search - Quick Zoom Click');
    this.zoomIntoProperty.emit();
  }

  public getInstanetUrl(): string {
    return '';
  }

  public openPropertyReport(): void {
    this.mixPanelServiceTracking('Search - Open Property Report Click');
    this.openReport.emit();
  }

  public onViewPhotoClick(): void {
    this.mixPanelServiceTracking('Search - View Photo Click');
    this.viewPhotos.emit();
  }

  public onReverseLinkClicked(): void {
    this.mixPanelServiceTracking('Search - Parcel Dimensions Click');
    this.reverseLink.emit();
  }

  public getPropertyPhotoUrlIfPresent(): string {
    return this.hasPhotos ? this.imageLoadService.getImageSource(this.parcelId, this.fipsCode,
      this.propertyData.MLS_PHOTO_PATH,
      this.propertyData.MLS_PHOTO_ID,
      this.propertyData.MLS_LISTING_NUMBER) : 'assets/search_property_image.png';
  }

  public hidePhoto(): void {
    this.photoHidden = true;
  }

  public getSellScoreValue(): string | boolean {
    if (this.selectedLayer && (this.selectedLayer.fieldCode === FieldCode.PREM_SELL_SCORE || this.selectedLayer.fieldCodeName === 'premSellScore')) {
      return false;
    }

    const valueForCheck = this.selectedProperty ?
      this.selectedProperty.premSellScore :
      this.propertyData[FieldCode.PREM_SELL_SCORE];

    const layerFilterList = MapUtils.getAvailableLayers(FieldCode.PREM_SELL_SCORE);

    if (!valueForCheck) {
      return;
    }

    //RP-7270 - fix info card for active type status.
    return this.getUpdatedSellScoreValue(layerFilterList, valueForCheck);
  }

  public getPremAttribute(): string {
    if (!this.selectedLayer) {
      return;
    }

    let parsedValue;

    let valueForCheck: string | number;

    if (this.propertyData && this.propertyData[this.selectedLayer.fieldCode]) {
      valueForCheck = this.propertyData[this.selectedLayer.fieldCode];
    } else if (this.selectedProperty) {
      valueForCheck = this.selectedProperty[this.selectedLayer.fieldCodeName];
    }

    if (!valueForCheck) {
      return;
    }

    switch (this.selectedLayer.fieldCode) {
      case FieldCode.PREM_RENT_SCORE:
        parsedValue = this.selectedLayer.filterListValues.find((filterValue) => {
          const matches = filterValue.code.match(/\d+/g);
          return +matches[0] <= +valueForCheck && +matches[1] >= +valueForCheck;
        }).code.match(/^[^\(]+/)[0];
        break;
      case FieldCode.PREM_SELL_SCORE:
        //RP-7270 - fix info card for active type status.
        parsedValue = this.getUpdatedSellScoreValue(this.selectedLayer.filterListValues, valueForCheck);
        break;
      case FieldCode.PREM_HOME_EQUITY:
      case FieldCode.PREM_LAST_SALE_PRICE:
      case FieldCode.PREM_PROPERTY_VALUE:
        if (+valueForCheck < 0) {
          parsedValue = 'Less then 0';
          break;
        }

        parsedValue = this.currencyPipe.transform(+valueForCheck, 'USD', 'symbol', '1.0-0');
        break;

      case FieldCode.PREM_OWNERSHIP_TIME:
      case FieldCode.PREM_MORTGAGE_AGE:
        parsedValue = this.selectedLayer.filterListValues.find((filterValue) => {
          const matches = filterValue.code.match(/\d+/g);
          const valueInYears = (+valueForCheck as number) / YEAR_DURATION;

          if (matches.length === 1) {
            if (this.selectedLayer.fieldCode === FieldCode.PREM_MORTGAGE_AGE) {
              return MAX_MORT_YEARS <= valueInYears;
            } else {
              return MAX_OWNER_YEARS <= valueInYears;
            }
          }

          return +matches[0] <= valueInYears && +matches[1] >= valueInYears;
        }).code + ` (${valueForCheck} ${+valueForCheck > 1 ? 'days' : 'day'})`;
        break;
      case FieldCode.PREM_OWNER_OCCUPIED:
        if (valueForCheck === 'O') {
          parsedValue = 'Yes';
        } else {
          parsedValue = 'No';
        }

        break;
      default:
        parsedValue = this.selectedLayer && this.propertyData[this.selectedLayer.fieldCode] ?
                      this.propertyData[this.selectedLayer.fieldCode] : this.selectedProperty[this.selectedLayer.fieldCodeName];
    }

    return parsedValue;
  }

  public isHideWindowHookedUp() {
    return this.hideWindow.observers.length > 0;
  }

  private getUpdatedSellScoreValue(layerFilterList: Array<{ code: string, index: number }>, valueForCheck: string | number): any {
    if (this.checkMlsStatusActive(this.propertyData.MLS_STD_MLS_STATUS)) {
      return 'N/A';
    }

    return layerFilterList.find((filterValue: any) => {
      const matches = filterValue.code.match(/\d+/g);
      return +matches[0] <= +valueForCheck && +matches[1] >= +valueForCheck;
    }).code.match(/^[^\(]+/)[0];
  }

  private checkMlsStatusActive(mlsStatus: string): boolean {
    return ACTIVE_INDICATOR_LIST.includes(mlsStatus);
  }

  private getPropertySummary(propertyData: PropertyData): Array<LabelValue> {
    this.store$.dispatch(new SetCurrentPageName('Search Result'));
    this.mixPanelServiceTracking('Search - Result Pin Click');

    return [
      this.formatterService.getBedsSummary(propertyData),
      this.formatterService.getBathsSummary(propertyData),
      ...this.formatterService.getSaleDataSummary(propertyData)
    ];
  }

  private mixPanelServiceTracking(eventId: string) {
    this.store$
      .pipe(
        select(selectSidePanelState)
      )
      .subscribe((sidePanelView: SidePanelView) => {
        this.sidePanelTabOpened = sidePanelView.sidePanelTabOpened;
      });

    this.store$.select(selectSelectedGeos).subscribe((result) => this.countyList = result.map((x) => x.fips));

    setTimeout( () => {
      this.mixpanelService.track(eventId, {
        propertyIdentifier: this.parcelId,
        countyList: this.countyList,
        searchType: this.sidePanelTabOpened
      });
    }, ReportsMixPanelUtil.TIME_DELAY_FOR_MIXPANEL );
  }

  public isItemSaved(item: PropertyInformation): boolean {
    return this.savedPropertyIdList.includes(PropertyUtil.getCombinedIdFromPropertyInformation(item));
  }

  public toggleSaved([property, saved]: [PropertyInformation, boolean]): void {
    if (saved) {
      const updatedSavedProperties = this.savedProperties.filter((propertyInformation: PropertyInformation) =>
        PropertyUtil.getCombinedIdFromPropertyInformation(propertyInformation)
        !== PropertyUtil.getCombinedIdFromPropertyInformation(property));
      this.mixPropertyPanelServiceTracking('Property Unstarred', property);
      this.store$.dispatch(new UpdateSavedProperties(updatedSavedProperties));
      this.isFavorite = false;
    } else {
      if (this.savedProperties.length < MAX_NUMBER_OF_SAVED_PROPERTIES) {
        const updatedSavedProperties = [property, ...this.savedProperties];
        this.mixPropertyPanelServiceTracking('Property Starred', property);
        this.store$.dispatch(new UpdateSavedProperties(updatedSavedProperties));
        this.isFavorite = true;
        showGlobalAlert('The property has been added to the saved property page.', 'alert-success', '', 3000);
      } else {
        showGlobalAlert('Sorry, you can only select up to 50 properties. ' +
          'Please de-select other saved properties and try again.', 'alert-warning', '', 3000);
      }
    }
  }

  private mixPropertyPanelServiceTracking(eventId: string, propertyInformation: PropertyInformation) {
    const propertyId = PropertyUtil.getCombinedIdFromPropertyInformation(propertyInformation);
    this.mixpanelService.track(eventId, {
      propertyIdentifier: propertyId,
      mlsStatus: propertyInformation.propertyData.MLS_STD_MLS_STATUS
    });
  }
}
